<template>
  <div>
    <div>
      <div class="md-layout md-gutter">
        <div class="md-layout-item md-size-50">
          <md-field :class="!checkPercentage(typeInfo.cover) ? 'md-invalid' : ''">
            <label for="cover">{{ $t("typeInfo.cover.nonwood") }} %</label>
            <md-input type="number" name="cover" v-model="typeInfo.cover" />
            <span class="md-error">{{ $t("error.errorRange") }} (0-100)</span>
          </md-field>
        </div>
        <div class="md-layout-item md-size-50">
          <md-field>
            <select v-model="typeInfo.plot_area" name="plotArea">
              <option disabled value="">{{ $t("typeInfo.plotArea") }}</option>
              <option v-for="area in areas" :value="area" :key="area">{{ area }} m²</option>
            </select>
          </md-field>
        </div>
      </div>
      <div v-for="(ts, i) in typeInfo.species" :key="i" class="md-layout md-gutter">
        <div class="md-layout-item md-size-90" v-if="ts !== null">
          <md-field>
            <std-autocomplete
              v-if="loadStd"
              :data="stdList"
              :label="$t('typeInfo.species')"
              :initialValue="ts.specie_std4"
              :initialDisplay="initSpecie[i]"
              :index="i"
              @result="getSpecie"></std-autocomplete>
          </md-field>
        </div>
        <div class="md-layout-item md-size-10" v-if="ts !== null">
          <md-icon @click.native="removeSpecie(i)" v-show="btn.rm">clear</md-icon>
        </div>

        <div class="md-layout-item md-size-33" v-if="ts !== null">
          <md-field :class="!checkRange(ts.individuals_nr, 0, 99999) ? 'md-invalid' : ''">
            <label for="individualsNr">{{ $t("typeInfo.specieIndividuals") }}</label>
            <md-input type="number" name="individualsNr" v-model="ts.individuals_nr" />
            <span class="md-error">{{ $t("error.errorRange") }} (0-99999)</span>
          </md-field>
        </div>
        <div class="md-layout-item md-size-33" v-if="ts !== null">
          <md-field :class="!checkRange(ts.avg_diameter, 0, 10) ? 'md-invalid' : ''">
            <label for="avd">{{ $t("typeInfo.specieAvd") }} (cm)</label>
            <md-input type="number" step="0.01" name="avd" v-model="ts.avg_diameter" />
            <span class="md-error">{{ $t("error.errorRange") }} (0-10)</span>
          </md-field>
        </div>
        <div class="md-layout-item md-size-33" v-if="ts !== null">
          <md-field :class="!checkAvh(ts.avg_height) ? 'md-invalid' : ''">
            <label for="avh">{{ $t("typeInfo.specieAvh") }} (cm)</label>
            <md-input type="number" step="0.01" name="avh" v-model="ts.avg_height" />
            <span class="md-error">{{ $t("error.errorRange") }} (0-300)</span>
          </md-field>
        </div>
      </div>

      <div class="md-layout md-gutter md-alignment-center-center">
        <md-button class="md-raised md-primary" @click="addSpecie()" v-show="btn.add">{{ $t("global.add") }} {{ $t('global.specie') }}</md-button>
      </div>
    </div>

    <error-dialog :show="error" :text="$t('error.dataCheck')" @closeError="error = false" />
  </div>
</template>

<script>
import StdAutocomplete from '@/components/StdAutocomplete'
import ErrorDialog from '@/components/ErrorDialog'

import EventBus from '@/event-bus'
import { getDomain } from '@/assets/scripts/domainsManager'
import { formatList, getValue, checkPercentage, checkAvh, checkRange, normalize } from '@/assets/scripts/formatData'

const STD4 = 'std4'

const SPECIE = {
  specie_std4: '',
  individuals_nr: null,
  avg_diameter: null,
  avg_height: null
}

export default {
  props: ['radius', 'nw'],
  components: {
    StdAutocomplete,
    ErrorDialog
  },
  data: () => ({
    typeInfo: {
      cover: null,
      plot_area: 25,
      plot_radius: null,
      species: [{ ...SPECIE }]
    },
    areas: [
      25,
      200,
      400
    ],
    stdList: [],
    btn: {
      rm: false,
      add: true
    },
    edit: false,
    loadStd: false,
    initSpecie: [],
    checkPercentage: checkPercentage,
    checkAvh: checkAvh,
    checkRange: checkRange,
    error: false
  }),
  mounted () {
    this.typeInfo.plot_radius = this.radius
    this.$store.state.plot.id === null ? this.edit = false : this.edit = true

    this.setLists()

    if (this.edit) { // edit mode
      if (this.nw) { // enter with button
        if (this.$store.state.plot.typeInfo.nonwood !== undefined) {
          this.typeInfo = this.$store.state.plot.typeInfo.nonwood
          this.setSpecie()
        } else {
          this.$store.commit('plot/setNonwood', this.typeInfo)
        }
      } else {
        this.typeInfo = this.$store.state.plot.typeInfo
        this.setSpecie()
      }
    }

    this.$emit('area', this.typeInfo.plot_area)

    EventBus.$on('save', () => {
      this.saveData()
    })
  },
  computed: {
    area () {
      return this.typeInfo.plot_area
    }
  },
  watch: {
    area () {
      this.$emit('area', this.typeInfo.plot_area)
    },
    radius: function () {
      this.typeInfo.plot_radius = this.radius
    }
  },
  methods: {
    saveData: function () {
      if (this.checkData()) {
        this.typeInfo.species = this.typeInfo.species.filter(el => {
          return el != null
        })
        if (this.typeInfo.cover !== null) this.typeInfo.cover = normalize(this.typeInfo.cover, 0)
        this.typeInfo.species.forEach(item => {
          if (item.individuals_nr !== null) item.individuals_nr = normalize(item.individuals_nr, 0)
        })

        this.edit ? this.$emit('typeInfo', null) : this.$emit('typeInfo', this.typeInfo)
      } else {
        this.error = true
      }
    },

    setLists: function () {
      getDomain(STD4).then(data => {
        this.stdList = formatList(data[this.$i18n.locale])
        if (this.edit) {
          this.typeInfo.species.forEach((specie, i) => {
            this.initSpecie[i] = getValue(this.stdList, specie.specie_std4)
          })
        }
        this.loadStd = true
      })
    },

    addSpecie: function () {
      this.typeInfo.species.push({ ...SPECIE })
      this.btn.rm = true
      if (this.getLength() === 3) {
        this.btn.add = false
      }
    },

    removeSpecie: function (index) {
      this.$set(this.typeInfo.species, index, null)
      this.btn.add = true
      if (this.getLength() === 1) {
        this.btn.rm = false
      }
    },

    setSpecie: function () {
      if (this.getLength() > 1) {
        this.btn.rm = true
        if (this.getLength() === 3) {
          this.btn.add = false
        }
      }
    },

    getSpecie: function (value, index) {
      this.typeInfo.species[index].specie_std4 = value
    },

    getLength: function () {
      let length = 0
      this.typeInfo.species.filter(el => {
        if (el != null) length++
      })

      return length
    },

    checkData: function () {
      let checkSpecie = true
      this.typeInfo.species.forEach(specie => {
        if (specie !== null) {
          if (!checkRange(specie.individuals_nr, 0, 99999)) checkSpecie = false
          if (!checkRange(specie.avg_diameter, 0, 10)) checkSpecie = false
          if (!checkAvh(specie.avg_height)) checkSpecie = false
        }
      })

      return checkPercentage(this.typeInfo.cover) && checkSpecie
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@/assets/style/arcadia_theme.scss';
</style>
